import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState, type FormEvent, type MouseEvent } from 'react'
import { Link } from '@tanstack/react-router'
import { ApiError } from '../../api/client'
import {
  agentReleasesQuery,
  agentReleasesQueryKey,
  forceUpdateHosts,
  publishAgent,
  revertAgent,
  type AgentReleasesPage as PageData,
  type FleetHost,
  type FleetPulse,
  type PackageVersions,
  type UpdateLogRow,
  type WriteOutcome,
} from '../../api/settingsAgentReleases'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { Forbidden, isForbidden } from '../../ui/Forbidden'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import './AgentReleasesPage.css'
import { UpdaterRollout } from './UpdaterRollout'

/**
 * Settings -> Agent Releases, mutat din
 * `views/version-3/settings/agent_releases/{index,fleet}.php` si din blocurile
 * de JavaScript din capul lor.
 *
 * Ecranul sta la ACEEASI adresa ca in portal - `/app/settings/agent_releases` -
 * fiindca aia e scrisa in `Services\Navigation\SettingsMenu`.
 *
 * POARTA E A PROPRIETARILOR, si e un NUME, nu un drept din schema:
 * `can_manage_unix_agent_releases()`. Aici nu se verifica nimic in ecran - un
 * 403 de la server iese "No access rights.", ca `show_error('Forbidden', 403)`.
 *
 * -----------------------------------------------------------------------------
 * CE FACE ECRANUL, DE SUS IN JOS
 * -----------------------------------------------------------------------------
 *
 *  1. RANDUL DE SUS spune ce versiune e publicata acum, si atat. Langa el,
 *     Publish (rosu: singurul buton care schimba ce ajunge pe toate masinile),
 *     apoi cutiile celor trei sisteme, apoi Update log si Agent Install.
 *  2. PUBLICAREA e un ecran, nu o coloana: apasat Publish, lista se da la o
 *     parte si raman randul de sus si formularul. Se intampla de cateva ori pe
 *     luna; o coloana goala langa lucrurile de zi cu zi ar fi fost mai rea.
 *  3. VERSIUNILE DE DINAINTE, pe pachet, cu Revert. Cea publicata acum e sus,
 *     la "Current version", si nu se mai repeta aici.
 *  4. FLOTA, cu bife si buton de Update pe randurile ramase in urma.
 *  5. GRAFICELE din dreapta: inel (versiune), scor (vorbire), trepte (CPU/RAM).
 *  6. JURNALUL vine din dreapta, ca sertar, cand se cere.
 *
 * -----------------------------------------------------------------------------
 * CE S-A MUTAT ALTFEL, SI DE CE
 * -----------------------------------------------------------------------------
 *
 *  - BUTONUL DE ACTUALIZARE IN LOT sta in bara de sus a listei, nu in capul
 *    coloanei "Actions". `ui/DataTable` deseneaza capetele din `columns`, si
 *    acolo nu incape un buton care apare si dispare dupa cate randuri sunt
 *    bifate. Purtarea e aceeasi: apare la prima bifa, spune cate, dispare cand
 *    nu mai e nimic bifat.
 *  - `data-sortkey` NU MAI EXISTA. Era un ocol in jurul lui DataTables, care isi
 *    schimba izvorul de date cand vedea `data-order` pe o celula si atunci
 *    ColReorder nu mai stia sa mute coloana. Aici ordonarea se face pe numere,
 *    inainte de desenare, deci ocolul nu mai are ce sa ocoleasca.
 */

const TABLE_ID = 'odArFleetTable'

/** `$kindLabel` din macheta. */
const KIND_LABEL: Record<string, string> = { manual: 'Manual', auto: 'Auto', force: 'Force' }

export function esc(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

function failed(error: unknown): string {
  return error instanceof ApiError && error.message ? error.message : 'Something went wrong.'
}

/* -------------------------------------------------------------------------
 * Flota
 * ---------------------------------------------------------------------- */

/**
 * Pragurile sunt ALE AGENTULUI, nu ale masinii: la noi zece la suta din
 * procesor e mult, iar nouazeci ar fi o pana. De-aia nu se folosesc aceleasi
 * trepte ca la gazda.
 */
function cpuBand(cpu: number): string {
  if (cpu >= 25) return ' is-hot'
  if (cpu >= 10) return ' is-warm'
  return ''
}

function ramBand(mb: number): string {
  if (mb >= 250) return ' is-hot'
  if (mb >= 100) return ' is-warm'
  return ''
}

function nf(value: number, digits = 2): string {
  return value.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })
}

/** Lamurirea de la trecerea cu mausul peste cele doua coloane de consum. */
function agentNote(host: FleetHost): string {
  let note = host.agentAt === '' ? '' : `sampled ${host.agentAt}`

  if (host.agentServiceCpu !== null) note += ` · service ${nf(host.agentServiceCpu)}%`
  if (host.agentServiceMb !== null) note += ` / ${nf(host.agentServiceMb)} MB`

  return note
}

/**
 * Memoria agentului, raportata la a masinii. Douazeci si doi de megaocteti nu
 * spun nimic singuri; scrisi "22 / 2132 MB", se citesc dintr-o data. Sub un
 * pixel se lasa totusi un pic de culoare: o bara goala s-ar citi "nu se stie",
 * nu "aproape nimic".
 */
function ramShare(host: FleetHost): number {
  if (host.agentMb === null) return 0
  if (host.ramTotal) return Math.max(1.5, Math.min(100, (host.agentMb / host.ramTotal) * 100))
  return Math.min(100, host.agentMb)
}

export function fleetColumns(selected: Set<number>, canUpdate: boolean): TableColumn[] {
  return [
    {
      index: 0,
      title: '',
      titleHtml: '<input type="checkbox" id="odArFleetAll" title="Select all">',
      orderable: false,
      visible: true,
      width: 28,
      className: 'od-ar-fleet-check',
      html: (row) => {
        const host = row as unknown as FleetHost
        return `<input type="checkbox" class="od-ar-fleet-one" value="${host.id}"${selected.has(host.id) ? ' checked' : ''}>`
      },
    },
    {
      /*
       * Semnul sistemului, inaintea numelui. Coloana OS scrie tot, dar cand
       * cauti cu ochiul serverele de Linux dintr-o lista de douazeci, un semn se
       * prinde mai repede decat un cuvant. Titlul ramane gol: semnele se explica
       * singure, si fiecare are numele in lamurire.
       */
      index: 1,
      title: '',
      orderable: true,
      visible: true,
      width: 28,
      className: 'od-ar-fleet-os-icon',
      html: (row) => {
        const host = row as unknown as FleetHost
        return `<i class="${esc(host.osIcon)}" aria-hidden="true" title="${esc(host.osName)}"></i>`
          + `<span class="sr-only">${esc(host.osName)}</span>`
      },
    },
    {
      index: 2,
      title: 'Hosts',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => {
        const host = row as unknown as FleetHost
        const name = host.name !== '' ? host.name : `#${host.id}`
        const company = host.company.trim() === ''
          ? ''
          : `<span class="od-ar-fleet-company">${esc(host.company)}</span>`

        return `<a href="/app/inventoree/device/${host.id}">${esc(name)}</a>${company}`
      },
    },
    {
      index: 3,
      title: 'OS',
      orderable: true,
      visible: true,
      className: 'a-left od-ar-os',
      html: (row) => {
        const host = row as unknown as FleetHost
        return esc(host.os !== '' ? host.os : '—')
      },
    },
    {
      index: 4,
      title: 'Version',
      orderable: true,
      visible: true,
      className: 'od-ar-mono',
      html: (row) => esc(String((row as unknown as FleetHost).version || '—')),
    },
    {
      index: 5,
      title: 'Agent CPU Load',
      titleHtml: '<span class="od-ar-fleet-col" title="What our agent itself uses on that machine">Agent CPU Load</span>',
      orderable: true,
      visible: true,
      className: 'od-ar-load',
      html: (row) => {
        const host = row as unknown as FleetHost
        if (host.agentCpu === null) return '<span class="text-muted">—</span>'

        return `<span class="od-ar-load-bar${cpuBand(host.agentCpu)}" title="${esc(agentNote(host))}">`
          + `<span style="width: ${host.agentCpu}%"></span></span>`
          + `<span class="od-ar-load-num">${nf(host.agentCpu)}%</span>`
      },
    },
    {
      index: 6,
      title: 'Agent RAM Usage',
      titleHtml: '<span class="od-ar-fleet-col" title="Memory our agent itself holds on that machine">Agent RAM Usage</span>',
      orderable: true,
      visible: true,
      className: 'od-ar-load',
      html: (row) => {
        const host = row as unknown as FleetHost
        if (host.agentMb === null) return '<span class="text-muted">—</span>'

        const of = host.ramTotal
          ? `<span class="od-ar-load-of"> / ${host.ramTotal.toLocaleString('en-US')}</span>`
          : ''

        return `<span class="od-ar-load-bar${ramBand(host.agentMb)}" title="${esc(agentNote(host))}">`
          + `<span style="width: ${ramShare(host)}%"></span></span>`
          + `<span class="od-ar-load-num">${nf(host.agentMb)}${of} MB</span>`
      },
    },
    {
      index: 7,
      title: 'Status',
      titleHtml: '<span class="od-ar-fleet-col" title="Up to date, catching up, or stuck - same split as the fleet chart">Status</span>',
      orderable: true,
      visible: true,
      className: 'od-ar-status',
      html: (row) => {
        const host = row as unknown as FleetHost
        const why = host.stuck ? ' title="Not up to date, and nothing has arrived for more than 24 hours"' : ''

        return `<span class="label label-${esc(host.statusBadge)}"${why}>${esc(host.statusLabel)}</span>`
      },
    },
    {
      index: 8,
      title: 'Auto-Update',
      orderable: true,
      visible: true,
      html: (row) => ((row as unknown as FleetHost).auto ? 'On' : 'Off'),
    },
    {
      index: 9,
      title: 'Installed',
      titleHtml: '<span class="od-ar-fleet-col" title="When the agent first reported from this machine">Installed</span>',
      orderable: true,
      visible: true,
      className: 'text-muted od-ar-when',
      html: (row) => esc(String((row as unknown as FleetHost).installed || '—')),
    },
    {
      index: 10,
      title: 'Last update',
      titleHtml: '<span class="od-ar-fleet-col" title="When the agent last changed version">Last update</span>',
      orderable: true,
      visible: true,
      /* Latime fixa, cat data cu ora: "at install" trece pe randul doi, altfel
         coloana se latea sau se strangea dupa ce randuri ajungeau pe pagina
         la ordonare, si tot tabelul sarea (Roxana, 6 octombrie 2026). */
      width: 150,
      className: 'text-muted od-ar-when od-ar-when-fixed',
      html: (row) => {
        const host = row as unknown as FleetHost
        /* Proaspat instalat = ultima actualizare E instalarea. Se spune si de
           unde vine data, ca sa nu para o actualizare care n-a avut loc. */
        const note = host.updatedAtInstall
          ? '<span class="od-ar-when-note" title="No update yet: this is the version it was installed with">at install</span>'
          : ''

        return `${esc(host.updated || '—')}${note}`
      },
    },
    {
      index: 11,
      title: 'Last seen',
      orderable: true,
      visible: true,
      className: 'text-muted od-ar-when',
      html: (row) => {
        const host = row as unknown as FleetHost
        /* Bulina rosie din bara numara exact randurile astea; fara semnul de
           aici, numarul de sus ar fi o alarma pe care nimeni n-o poate urmari
           pana la masina care a produs-o. */
        const silent = host.silent
          ? '<span class="od-ar-silent" title="No word for more than three days">silent</span>'
          : ''

        return `<span title="${esc(host.lastSeenExact)}">${esc(host.lastSeen || '—')}</span> ${silent}`
      },
    },
    {
      index: 12,
      title: 'Actions',
      orderable: false,
      visible: true,
      width: 130,
      className: 'od-ar-fleet-actions',
      html: (row) => {
        const host = row as unknown as FleetHost

        if (host.status === 'pending') {
          return '<span class="od-ar-fleet-requested">Update Requested</span>'
        }

        /* Butonul apare numai la masinile ramase in urma: pe una la zi n-are ce
           sa actualizeze, iar un buton care nu face nimic invata omul ca
           butoanele de aici nu fac nimic. */
        if (canUpdate && host.needsUpdate) {
          return `<button type="button" class="btn btn-default btn-sm od-ar-fleet-row-update" data-id="${host.id}">Update</button>`
        }

        return ''
      },
    },
  ]
}

/** Dupa ce se ordoneaza fiecare coloana - numerele pregatite de server. */
const SORT_KEYS: Array<((host: FleetHost) => number | string) | null> = [
  null,
  (h) => h.osName.toLowerCase(),
  (h) => h.name.toLowerCase(),
  (h) => h.os.toLowerCase(),
  (h) => h.version,
  (h) => (h.agentCpu === null ? -1 : h.agentCpu),
  (h) => (h.agentMb === null ? -1 : h.agentMb),
  (h) => h.statusSort,
  (h) => (h.auto ? 1 : 0),
  (h) => h.installedSort,
  (h) => h.updatedSort,
  (h) => h.lastSeenSort,
  null,
]

/* -------------------------------------------------------------------------
 * Ecranul
 * ---------------------------------------------------------------------- */

export function AgentReleasesPage() {
  const queryClient = useQueryClient()
  const page = useQuery(agentReleasesQuery())

  if (isForbidden(page.error)) return <Forbidden />
  if (page.error) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> {failed(page.error)}
      </div>
    )
  }
  if (!page.data) return <Spinner />

  return (
    <AgentReleases
      data={page.data}
      reload={() => void queryClient.invalidateQueries({ queryKey: agentReleasesQueryKey })}
    />
  )
}

function AgentReleases({ data, reload }: { data: PageData; reload(): void }) {
  const [publishing, setPublishing] = useState(false)
  const [logOpen, setLogOpen] = useState(false)
  const [selected, setSelected] = useState<Set<number>>(() => new Set())

  const current = data.manifest.version

  return (
    <div className="od-ar">
      <Head
        data={data}
        publishing={publishing}
        onPublish={() => setPublishing(true)}
        onLog={() => setLogOpen(true)}
      />

      {data.updaterManaged ? <ManagedNotice /> : null}

      {publishing && !data.updaterManaged ? (
        <PublishForm data={data} onCancel={() => setPublishing(false)} onPublished={reload} />
      ) : null}

      <div className="od-ar-split">
        <div className="od-ar-split-main">
          {data.updater ? <UpdaterRollout updater={data.updater} /> : null}
          <Versions packages={data.versions} onReverted={reload} locked={data.updaterManaged === true} />
          {!publishing && (
            <Fleet
              data={data}
              selected={selected}
              onSelected={setSelected}
              onUpdated={reload}
            />
          )}
        </div>
        {data.pulse.total > 0 && <Sparks pulse={data.pulse} />}
      </div>

      <UpdateLog rows={data.history} open={logOpen} onClose={() => setLogOpen(false)} current={current} />
    </div>
  )
}

/* -------------------------------------------------------------------------
 * Randul de sus
 * ---------------------------------------------------------------------- */

function Head({
  data,
  publishing,
  onPublish,
  onLog,
}: {
  data: PageData
  publishing: boolean
  onPublish(): void
  onLog(): void
}) {
  const current = data.manifest.version
  const updated = data.manifest.updated
  const unread = useUnread(data.history)

  return (
    <div className="od-ar-head">
      <span className={`od-ar-head-now${current === '' ? ' is-none' : ''}`}>
        <span className="od-ar-head-label">Current version</span>
        {current !== '' ? (
          <span className="od-ar-head-ver">{current}</span>
        ) : (
          <span className="od-ar-head-ver is-none">None</span>
        )}
        <span className="od-ar-head-meta">
          {current === '' ? 'nothing published yet' : updated !== '' ? `published ${updated}` : 'published as latest'}
        </span>
      </span>

      {/*
        Publish, chiar langa versiune: randul se citeste ca o propozitie - asta e
        publicat, si asta apesi ca sa publici altceva. Rosu fiindca e singurul
        buton de pe ecran care schimba ce ajunge pe toate masinile din flota.
      */}
      {!publishing && !data.updaterManaged && (
        <button type="button" id="odArNewVersion" className="btn btn-sm od-ar-new-btn" onClick={onPublish}>
          Publish
        </button>
      )}

      {Object.entries(data.osBuilds).map(([osName, builds]) => (
        <span className="od-ar-os" key={osName}>
          <span className="od-ar-os-name">{osName}</span>
          {builds.map(([slot, label]) => {
            const file = data.manifest.files[slot]
            return (
              <span
                key={slot}
                className={`od-ar-chip ${file ? 'is-on' : 'is-off'}`}
                title={file ? `on disk · ${file.mb}` : 'not uploaded'}
              >
                {label}
              </span>
            )
          })}
        </span>
      ))}

      <span className="od-ar-log-wrap">
        <button
          type="button"
          id="odArLogOpen"
          className="btn btn-default btn-sm od-ar-log-btn"
          title={unread.count > 0 ? `${unread.count} ${unread.count === 1 ? 'update' : 'updates'} since you last opened this` : undefined}
          onClick={() => {
            unread.markSeen()
            onLog()
          }}
        >
          Update log
          {unread.count > 0 && (
            <span className="od-ar-log-badge">{unread.count > 99 ? '99+' : unread.count}</span>
          )}
        </button>
      </span>

      {/*
        Drumul catre pagina de instalare, chiar de aici: intrebarea care vine
        imediat dupa "ce versiune are flota" e "si pe masina asta noua cum il
        pun?". Discret dinadins - se apasa o data la o masina noua.
      */}
      <Link
        to="/settings/download_agent"
        className="btn btn-default btn-sm od-ar-install-btn"
        title="Where to get the agent for a new machine"
      >
        Agent Install
      </Link>
    </div>
  )
}

/**
 * Necititele din jurnal: cate actualizari s-au intamplat de cand n-ai mai
 * deschis sertarul.
 *
 * Se tine minte cel mai mare numar de eveniment vazut, in `localStorage` - la
 * fel ca insigna de pe Change Log din subsol. NU pe server: e "de cand am
 * deschis EU ultima oara", nu o stare a produsului. Doi oameni care se uita la
 * aceeasi flota au fiecare necititele lui.
 *
 * Prima deschidere a paginii de catre cineva nou nu arata "toate sunt noi": se
 * scrie tacut ce e acum, si de la urmatoarea actualizare se numara.
 */
function useUnread(rows: UpdateLogRow[]) {
  const key = 'od-ar-log-seen'
  const newest = useMemo(() => rows.reduce((top, row) => Math.max(top, row.id), 0), [rows])
  const [seen, setSeen] = useState<number | null>(() => {
    try {
      const raw = window.localStorage.getItem(key)
      return raw === null ? 0 : Number.parseInt(raw, 10) || 0
    } catch {
      /* Fereastra privata, sau memoria oprita: atunci nu stim de cand n-a mai
         deschis, si nu inventam - nu se arata nicio insigna. */
      return null
    }
  })

  useEffect(() => {
    if (seen !== 0 || newest === 0) return
    try {
      window.localStorage.setItem(key, String(newest))
    } catch {
      /* Nu se poate scrie - atunci nu se poate nici numara. */
    }
    setSeen(newest)
  }, [seen, newest])

  const count = seen === null || seen === 0 ? 0 : rows.filter((row) => row.id > seen).length

  return {
    count,
    markSeen() {
      try {
        window.localStorage.setItem(key, String(newest))
      } catch {
        /* Fara memorie in browser, insigna se intoarce la urmatoarea incarcare.
           Neplacut, dar nu gresit. */
      }
      setSeen(newest)
    },
  }
}

/* -------------------------------------------------------------------------
 * Publicarea
 * ---------------------------------------------------------------------- */

const RISKS = (what: string): string =>
  `${what}:\n\n`
  + '• The binary runs AS ROOT on the machines that take it.\n'
  + '• It reaches every machine on Auto-Update, on its own.\n'
  + '• A broken build can take the fleet offline - the agent is what brings it back.\n'
  + '• It cannot be undone from here: it takes another, higher version to replace it.\n\n'
  + 'Press OK to confirm you have read this.'

/**
 * Formularul de publicare. Un slot lasat gol NU se sterge si NU se copiaza in
 * altul: pastrand acelasi numar de versiune, se adauga un binar langa cele deja
 * publicate - Windows Server dupa Linux, sau invers.
 *
 * ZAVORUL: serverul raspunde `needCode`, si atunci se arata ce se publica, se
 * cere invoirea si codul, si se trimite ACEEASI `FormData` - cu fisierele deja
 * citite - cu cele doua campuri in plus.
 */
function PublishForm({ data, onCancel, onPublished }: { data: PageData; onCancel(): void; onPublished(): void }) {
  const form = useRef<HTMLFormElement>(null)
  const current = data.manifest.version
  const [version, setVersion] = useState(current !== '' ? current : '26.8.28.1')
  const [busy, setBusy] = useState(false)

  const send = async (body: FormData): Promise<void> => {
    const out: WriteOutcome = await publishAgent(body)

    if (out.needCode) {
      if (!window.confirm(RISKS(`Publishing ${version}`))) return

      const code = window.prompt(out.message ?? 'Enter the code sent to your email.', '')
      if (code === null) return

      body.set('code', code.trim())
      body.set('agree', 'I agree')
      await send(body)
      return
    }

    toast(
      current !== '' && version === current ? `Binaries added to ${version}` : `Agent ${version} published`,
      'Success',
    )
    onPublished()
    onCancel()
  }

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const node = form.current
    if (node === null) return

    const body = new FormData(node)
    let chosen = 0

    node.querySelectorAll<HTMLInputElement>('input[type="file"]').forEach((input) => {
      if (input.files && input.files.length > 0) chosen += 1
    })

    if (chosen === 0) {
      toast('Choose at least one binary. Empty slots keep what is already published.', 'Error', 'error')
      return
    }

    const question = current !== '' && version === current
      ? `Add or replace the chosen binaries on ${version}. Slots you leave empty stay as they are. Hosts are not forced to update.`
      : `Publish ${version} as latest. Slots you leave empty keep their current files. Hosts with Auto-Update will pick it up; Force is only the Update button.`

    if (!window.confirm(question)) return

    setBusy(true)
    void send(body)
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <div className="portlet light bordered od-ar-publish" id="odArUpload">
      <div className="portlet-title">
        <div className="caption">Publish binaries</div>
      </div>
      <div className="portlet-body">
        <form id="unixAgentReleaseForm" ref={form} method="post" encType="multipart/form-data" onSubmit={submit}>
          <div className="form-group od-ar-version">
            <label htmlFor="unixAgentVersion">Version</label>
            <input
              className="form-control"
              id="unixAgentVersion"
              name="version"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="26.8.28.1"
              required
              pattern="\d+\.\d+\.\d+(\.\d+)?"
              autoFocus
            />
            <p className="help-block">
              Keep the number you already published to add another system beside it &mdash; Windows Server after Linux,
              or Windows 10/11 after Server. A slot left empty is not removed and is not copied into another package.
            </p>
          </div>

          {/*
            Casutele de incarcare, grupate pe platforme - aceeasi impartire ca
            in cutiile din capul paginii, din aceeasi definitie. Windows are
            doua sloturi: Server si 10/11, binare si foldere separate.
          */}
          <div className="od-ar-platforms">
            {Object.entries(data.osBuilds).map(([osName, builds]) => (
              <div className="od-ar-platform" key={osName}>
                <div className="od-ar-platform-name">{osName}</div>
                <div className="od-ar-platform-slots">
                  {builds.map(([slot, label]) => {
                    const field = Object.entries(data.fields).find(([, meta]) => meta[0] === slot)
                    if (field === undefined) return null

                    const [fieldName, meta] = field
                    const file = data.manifest.files[slot]

                    return (
                      <div className={`od-ar-slot${file ? ' is-on-disk' : ''}`} key={slot}>
                        <label htmlFor={`odArFile-${fieldName}`}>
                          {label} <span className="od-ar-arch">{meta[2]}</span>
                        </label>
                        <p className="od-ar-hint">
                          {file ? (
                            <>
                              <span className="od-ar-on-disk">{file.mb} on disk</span> &mdash; leave empty to keep
                            </>
                          ) : (
                            'not published yet'
                          )}
                        </p>
                        <input type="file" id={`odArFile-${fieldName}`} name={fieldName} className="form-control" />
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>

          <button type="submit" className="btn od-ar-publish-btn" disabled={busy}>
            {busy ? 'Publishing…' : 'Publish'}
          </button>{' '}
          <button type="button" id="odArUploadCancel" className="btn btn-default" onClick={onCancel}>
            Cancel
          </button>
        </form>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------
 * Versiunile de dinainte
 * ---------------------------------------------------------------------- */

/**
 * Numai versiunile de dinainte. Cea publicata acum e sus, la "Current version";
 * aici nu se mai repeta. Pe o linie: ultima anterioara, data, Revert. Chevronul
 * de la CAPATUL randului deschide restul istoricului - la capat, si nu langa
 * Revert, ca sa nu para ca tine de el.
 */
/**
 * Instalare tinuta de updater-ul on-prem (`OD_UPDATER_SHARED`): versiunile
 * agentilor le stabileste el. Publish si Revert sunt oprite si pe server
 * (`AgentReleases::MANAGED`); aici doar se spune de ce lipsesc.
 */
export function ManagedNotice() {
  return (
    <div className="alert alert-warning od-ar-managed" id="odArManaged" role="alert">
      <i className="fas fa-lock" aria-hidden="true"></i> Agent versions on this installation are set by the on-prem
      updater. Publish and Revert are turned off here: the next update would overrule them.
    </div>
  )
}

function Versions({
  packages,
  onReverted,
  locked = false,
}: {
  packages: PackageVersions[]
  onReverted(): void
  locked?: boolean
}) {
  const revert = useMutation({
    mutationFn: async (value: { pkg: string; label: string; version: string }) => {
      let out: WriteOutcome = await revertAgent(value.pkg, value.version)

      if (out.needCode) {
        if (!window.confirm(RISKS(`Reverting ${value.label} to ${value.version}`))) return null

        const code = window.prompt(out.message ?? 'Enter the code sent to your email.', '')
        if (code === null) return null

        out = await revertAgent(value.pkg, value.version, code.trim())
        if (out.needCode) {
          toast(out.message ?? 'Revert failed', 'Error', 'error')
          return null
        }
      }

      return value
    },
    onSuccess: (value) => {
      if (value === null) return
      toast(`${value.label} is back on ${value.version}`, 'Reverted')
      onReverted()
    },
    onError: (error) => toast(failed(error), 'Error', 'error'),
  })

  const ask = (pkg: string, label: string, version: string) => {
    if (!window.confirm(`Put ${label} back to ${version} as latest? Hosts with Auto-Update will take it.`)) return
    revert.mutate({ pkg, label, version })
  }

  const line = (pkg: string, label: string, version: string, updated: string, chevron: boolean) => (
    <>
      <span className="od-ar-versions-v">{version}</span>
      <span className="od-ar-versions-when">{updated}</span>
      <span className="od-ar-versions-act">
        <button
          type="button"
          className="btn od-ar-revert"
          disabled={revert.isPending || locked}
          title={locked ? 'Set by the on-prem updater' : undefined}
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            ask(pkg, label, version)
          }}
        >
          Revert
        </button>
      </span>
      {chevron && <i className="fas fa-chevron-right od-ar-versions-chev" aria-hidden="true"></i>}
    </>
  )

  return (
    <div className="portlet light bordered od-ar-versions" id="odArVersions">
      <div className="portlet-body od-ar-versions-row">
        <div className="od-ar-versions-caption">Previous</div>
        {packages.map((entry) => {
          const previous = entry.versions.filter((row) => !row.current)
          const head = previous[0]
          const rest = previous.slice(1)

          return (
            <div className="od-ar-versions-pkg" data-pkg={entry.pkg} key={entry.pkg}>
              <span className="od-ar-versions-name">{entry.label}</span>
              {entry.error !== '' ? (
                <p className="od-ar-versions-empty" title={entry.error}>
                  Not available: {entry.error}
                </p>
              ) : head === undefined ? (
                <p className="od-ar-versions-empty">No previous versions.</p>
              ) : rest.length > 0 ? (
                <details className="od-ar-versions-fold">
                  <summary className="od-ar-versions-line" title="Older versions">
                    {line(entry.pkg, entry.label, head.version, head.updated, true)}
                  </summary>
                  <ul className="od-ar-versions-hist">
                    {rest.map((row) => (
                      <li className="od-ar-versions-line" key={row.version}>
                        {line(entry.pkg, entry.label, row.version, row.updated, false)}
                      </li>
                    ))}
                  </ul>
                </details>
              ) : (
                <div className="od-ar-versions-line">
                  {line(entry.pkg, entry.label, head.version, head.updated, false)}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------
 * Lista
 * ---------------------------------------------------------------------- */

function Fleet({
  data,
  selected,
  onSelected,
  onUpdated,
}: {
  data: PageData
  selected: Set<number>
  onSelected(next: Set<number>): void
  onUpdated(): void
}) {
  const [state, setState] = useTableState(TABLE_ID, {
    start: 0,
    length: 25,
    /* `order: []` in portal - lista vine deja asezata de server, cu
       impotmolitele in cap. Coloana -1 inseamna "cum a venit". */
    order: { column: -1, dir: 'asc' },
    search: '',
  })

  const columns = useMemo(() => fleetColumns(selected, data.canUpdate), [selected, data.canUpdate])

  /* Cautarea, ordonarea si pagina se fac aici: randurile sunt toate in pagina,
     ca in portal, unde DataTables lucra tot pe ele. */
  const filtered = useMemo(() => {
    const q = state.search.trim().toLowerCase()

    if (q === '') return data.devices

    return data.devices.filter((host) =>
      `${host.name} ${host.company} ${host.os} ${host.version} ${host.statusLabel}`.toLowerCase().includes(q),
    )
  }, [data.devices, state.search])

  const sorted = useMemo(() => {
    const key = SORT_KEYS[state.order.column]
    if (!key) return filtered

    const rows = [...filtered]
    const sign = state.order.dir === 'asc' ? 1 : -1

    rows.sort((a, b) => {
      const left = key(a)
      const right = key(b)

      if (typeof left === 'string' || typeof right === 'string') {
        return sign * String(left).localeCompare(String(right))
      }

      return sign * (left - right)
    })

    return rows
  }, [filtered, state.order.column, state.order.dir])

  const shown = sorted.slice(state.start, state.start + state.length)

  const update = useMutation({
    mutationFn: (ids: number[]) => forceUpdateHosts(ids),
    onSuccess: () => {
      onSelected(new Set())
      onUpdated()
    },
    onError: (error) => toast(failed(error), 'Error', 'error'),
  })

  /*
   * Bifele si butoanele sunt HTML scris de coloana si pus cu `innerHTML`, exact
   * ca in portal - deci n-au `onClick` propriu, si clicul se prinde sus, pe
   * delegare, cum il prindea jQuery pe `document`.
   */
  const onClick = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement

    const all = target.closest('#odArFleetAll')
    if (all instanceof HTMLInputElement) {
      onSelected(all.checked ? new Set(sorted.map((host) => host.id)) : new Set())
      return
    }

    const one = target.closest('input.od-ar-fleet-one')
    if (one instanceof HTMLInputElement) {
      const next = new Set(selected)
      const id = Number(one.value)
      if (one.checked) next.add(id)
      else next.delete(id)
      onSelected(next)
      return
    }

    const row = target.closest('.od-ar-fleet-row-update')
    if (row instanceof HTMLElement) {
      event.preventDefault()
      /* Fara fereastra de confirmare, ca in portal: cererea pleaca, si lista se
         cere din nou - randul trece in "Update Requested". */
      update.mutate([Number(row.dataset.id ?? 0)])
    }
  }

  if (data.devices.length === 0) {
    return (
      <div className="portlet light bordered od-ar-fleet">
        <div className="portlet-title">
          <div className="caption">Fleet</div>
        </div>
        <div className="portlet-body">
          <div className="od-ar-empty">
            <i className="fas fa-server" aria-hidden="true"></i>
            No Linux or macOS agents found.
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="portlet light bordered od-ar-fleet" id="odArFleetWrap" onClick={onClick}>
      <div className="portlet-body">
        <DataTable
          id={TABLE_ID}
          columns={columns}
          rows={shown as unknown as Record<string, unknown>[]}
          total={sorted.length}
          fetching={update.isPending}
          state={state}
          onState={setState}
          rowId={(row) => `host_${Number(row.id)}`}
          emptyHint="Machines with the Go agent - Linux, macOS, Windows Server and Windows 10/11."
          /* `agent_releases/index.php` (buttons: colvis + sync): Columns si
             Refresh. Acolo Refresh reincarca pagina; aici reciteste ecranul. */
          footerButtons={{ colvis: true, refresh: onUpdated }}
          headerButtons={
            data.canUpdate && selected.size > 0 ? (
              <button
                type="button"
                className="btn btn-primary tk-header-extra od-ar-fleet-update"
                id="odArFleetUpdate"
                disabled={update.isPending}
                onClick={() => update.mutate([...selected])}
              >
                Update ({selected.size})
              </button>
            ) : undefined
          }
        />
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------
 * Graficele din dreapta
 * ---------------------------------------------------------------------- */

type Tier = [number, string, string, string]

function tiersOf(tiers: Tier[]) {
  const sum = tiers.reduce((total, tier) => total + Math.max(0, tier[0]), 0)

  return (
    <div className="od-ar-tiers">
      <div className="od-ar-tiers-rail" aria-hidden="true">
        {tiers
          .filter(([n]) => n >= 1)
          .map(([n, cls, label]) => (
            <i
              key={cls}
              className={cls}
              style={{ width: `${sum > 0 ? (n / sum) * 100 : 0}%` }}
              title={`${n} ${label}`}
            />
          ))}
      </div>
      <ul className="od-ar-tiers-legend">
        {tiers
          .filter(([n]) => n >= 1)
          .map(([n, cls, label, range]) => (
            <li className={cls} key={cls}>
              <i aria-hidden="true" />
              <b>{n}</b>
              <span>{label}</span>
              <em>{range}</em>
            </li>
          ))}
      </ul>
    </div>
  )
}

/** `$odArDonut` din macheta: un `conic-gradient` din numerele date. */
function donut(parts: Array<[number, string]>): string {
  const total = parts.reduce((sum, part) => sum + part[0], 0)

  if (total < 1) return '#e9ecef'

  const stops: string[] = []
  let at = 0

  for (const [n, color] of parts) {
    if (n < 1) continue
    const from = at
    at += (n / total) * 100
    stops.push(`${color} ${from.toFixed(2)}% ${at.toFixed(2)}%`)
  }

  return `conic-gradient(${stops.join(', ')})`
}

/**
 * Patru grafice separate, fiecare cu alt limbaj vizual: inel (versiune), scor
 * (vorbire), trepte (CPU/RAM). Acelasi panou pe toate le facea sa se confunde;
 * asa, fiecare raspunde la o intrebare diferita si arata diferit.
 */
function Sparks({ pulse }: { pulse: FleetPulse }) {
  const total = pulse.total
  const talkPct = total < 1 ? 0 : Math.round((pulse.talk.ok / total) * 100)
  const talkOkW = total > 0 ? (pulse.talk.ok / total) * 100 : 0
  const talkSilentW = total > 0 ? (pulse.talk.rest / total) * 100 : 0

  const loadTiers: Tier[] = [
    [pulse.load.extremely_low, 'is-xl', 'Extremely Low', '<1%'],
    [pulse.load.very_low, 'is-vl', 'Very Low', '1–2%'],
    [pulse.load.medium, 'is-med', 'Medium', '2–5%'],
    [pulse.load.high, 'is-hi', 'High', '>5%'],
    [pulse.load.none, 'is-none', 'no sample', '—'],
  ]

  const memTiers: Tier[] = [
    [pulse.memory.extremely_low, 'is-xl', 'Extremely Low', '<15 MB'],
    [pulse.memory.very_low, 'is-vl', 'Very Low', '15–25 MB'],
    [pulse.memory.low, 'is-low', 'Low', '25–50 MB'],
    [pulse.memory.medium, 'is-med', 'Medium', '50–100 MB'],
    [pulse.memory.high, 'is-hi', 'High', '>100 MB'],
    [pulse.memory.none, 'is-none', 'no sample', '—'],
  ]

  return (
    <aside className="od-ar-sparks" aria-label="Fleet at a glance">
      <article
        className="od-ar-chart od-ar-chart-fleet"
        title={`${pulse.fleet.current} on latest version, ${pulse.fleet.late} catching up, ${pulse.fleet.stuck} stuck`}
      >
        <div className="od-ar-chart-head">
          <strong>Fleet</strong>
          <em>
            {total} host{total === 1 ? '' : 's'}
          </em>
        </div>
        <div className="od-ar-fleet-body">
          <span
            className="od-ar-donut"
            style={{
              background: donut([
                [pulse.fleet.current, '#4c9f70'],
                [pulse.fleet.late, '#e0a32e'],
                [pulse.fleet.stuck, '#d9534f'],
              ]),
            }}
          >
            <span className="od-ar-donut-core">
              <b>{pulse.fleet.current}</b>
              <span>latest</span>
            </span>
          </span>
          <ul className="od-ar-fleet-legend">
            <li className="is-current">
              <i aria-hidden="true" />
              <b>{pulse.fleet.current}</b>
              <span>Latest Version</span>
            </li>
            <li className="is-late">
              <i aria-hidden="true" />
              <b>{pulse.fleet.late}</b>
              <span>catching up</span>
            </li>
            <li className="is-stuck">
              <i aria-hidden="true" />
              <b>{pulse.fleet.stuck}</b>
              <span>stuck</span>
            </li>
          </ul>
        </div>
      </article>

      <article
        className="od-ar-chart od-ar-chart-talk"
        title={`${pulse.talk.ok} heard from, ${pulse.talk.rest} silent (no word for more than three days)`}
      >
        <div className="od-ar-chart-head">
          <strong>Talking</strong>
          <em>last 3 days</em>
        </div>
        <div className="od-ar-talk-score">
          <b>{talkPct}%</b>
          <span>heard from</span>
        </div>
        <div className="od-ar-talk-rail" aria-hidden="true">
          {talkOkW > 0 && <i className="is-ok" style={{ width: `${talkOkW}%` }} />}
          {talkSilentW > 0 && <i className="is-bad" style={{ width: `${talkSilentW}%` }} />}
        </div>
        <div className="od-ar-talk-pair">
          <span className="is-heard">
            <b>{pulse.talk.ok}</b>
            <em>heard</em>
          </span>
          <span className={`is-silent${pulse.talk.rest < 1 ? ' is-zero' : ''}`}>
            <b>{pulse.talk.rest}</b>
            <em>silent</em>
          </span>
        </div>
      </article>

      <article
        className="od-ar-chart od-ar-chart-cpu"
        title="CPU: <1% Extremely Low, 1–2% Very Low, 2–5% Medium, >5% High"
      >
        <div className="od-ar-chart-head">
          <strong>CPU</strong>
          <em>agent load</em>
        </div>
        {tiersOf(loadTiers)}
      </article>

      <article
        className="od-ar-chart od-ar-chart-ram"
        title="RAM: <15 MB Extremely Low, 15–25 Very Low, 25–50 Low, 50–100 Medium, >100 High"
      >
        <div className="od-ar-chart-head">
          <strong>RAM</strong>
          <em>agent memory</em>
        </div>
        {tiersOf(memTiers)}
      </article>
    </aside>
  )
}

/* -------------------------------------------------------------------------
 * Sertarul cu jurnalul
 * ---------------------------------------------------------------------- */

/**
 * Jurnalul vine din dreapta, cand se cere - ca sertar, si nu ca a doua coloana:
 * se citeste rar, iar pana atunci lua jumatate de pagina de la lista pentru care
 * se intra aici. Se inchide cu Escape sau cu o apasare in afara lui, si nu misca
 * nimic sub el.
 *
 * Incepe SUB antetul portalului (50px): acoperit, omul ramane fara nicio iesire
 * in afara de X-ul sertarului.
 */
function UpdateLog({
  rows,
  open,
  onClose,
  current,
}: {
  rows: UpdateLogRow[]
  open: boolean
  onClose(): void
  current: string
}) {
  const [search, setSearch] = useState('')
  const [kind, setKind] = useState('all')

  useEffect(() => {
    if (!open) return

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }

    document.addEventListener('keydown', onKey)
    /* Cat timp sertarul e deschis, pagina de sub el nu se plimba: altfel
       inchiderea te lasa in alt loc decat cel din care ai plecat. */
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open, onClose])

  const q = search.trim().toLowerCase()
  const shown = rows.filter(
    (row) => (kind === 'all' || row.kind === kind) && (q === '' || row.search.includes(q)),
  )

  return (
    <>
      <div className="od-ar-drawer-veil" id="odArLogVeil" hidden={!open} onClick={onClose} />
      <aside className="od-ar-drawer" id="odArLogPanel" hidden={!open} aria-label="Update log">
        <div className="od-ar-drawer-head">
          <span className="od-ar-drawer-title">Update log</span>
          <button type="button" className="od-ar-drawer-close" id="odArLogClose" title="Close" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="od-ar-drawer-body" id="agent-updates">
          <div className="portlet light bordered">
            <div className="portlet-title">
              <div className="od-ar-log-tools">
                <input
                  type="search"
                  id="odArLogSearch"
                  className="form-control"
                  placeholder="Search host, company, version"
                  disabled={rows.length === 0}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <div className="od-ar-filters" id="odArLogFilters">
                  {['all', 'manual', 'auto', 'force'].map((one) => (
                    <button
                      type="button"
                      key={one}
                      className={kind === one ? 'is-on' : undefined}
                      onClick={() => setKind(one)}
                    >
                      {one === 'all' ? 'All' : KIND_LABEL[one]}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="portlet-body">
              {rows.length === 0 ? (
                <div className="od-ar-empty">
                  <i className="fas fa-history" aria-hidden="true"></i>
                  No updates logged yet.
                  <br />
                  After a host Assigns with a new version, it appears here.
                </div>
              ) : (
                <div className="od-ar-log-scroll">
                  <table className="table table-hover od-ar-log-table" id="odArLogTable">
                    {/*
                      "De la" si "la" sunt doua coloane, nu o propozitie. Scrise
                      impreuna, in sertar erau prima parte taiata pe dreapta - si
                      tocmai ele sunt intrebarea cu care se deschide jurnalul.
                    */}
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Host</th>
                        <th>Previous version</th>
                        <th>Updated to</th>
                        <th>Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shown.map((row, index) => (
                        <tr key={`${row.id}-${row.deviceId}-${index}`} data-kind={row.kind}>
                          <td className="text-muted">{row.when}</td>
                          <td>
                            <Link to="/inventoree/device/$id" params={{ id: String(row.deviceId) }}>
                              {row.host}
                            </Link>
                            {row.company !== '' && <span className="od-ar-fleet-company">{row.company}</span>}
                          </td>
                          <td className="od-ar-mono">
                            <span className="text-muted">{row.from !== '' ? row.from : '—'}</span>
                          </td>
                          <td className="od-ar-mono">
                            {row.to !== '' ? (
                              <strong className={row.to === current ? 'od-ar-is-latest' : undefined}>{row.to}</strong>
                            ) : (
                              row.message
                            )}
                          </td>
                          <td>
                            <span className={`od-ar-kind od-ar-kind-${row.kind}`}>{KIND_LABEL[row.kind] ?? 'Manual'}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>
    </>
  )
}
